import { Link } from "@/lib/router-compat";
import { lazy, Suspense } from "react";
import { ArrowRight, ShieldCheck, Lock, Truck, Sparkles, Factory, Building2, Gauge, BadgeCheck, Recycle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import heroVideo from "@/assets/hero-logo-animation.mp4";
import logo from "@/assets/logo.webp";
import howItWorks from "@/assets/how-it-works.webp";
import benefitPrice from "@/assets/benefit-price.jpg";
import benefitEscrow from "@/assets/benefit-escrow.jpg";
import benefitVerified from "@/assets/benefit-verified.jpg";
import sellerBand from "@/assets/seller-band.jpg";
import eprHero from "@/assets/epr-hero.jpg";
import { COMING_SOON_MATERIALS } from "@/data/comingSoonMaterials";
import LiveListingsShowcase from "@/components/landing/LiveListingsShowcase";
import { HeroMascot } from "@/components/landing/HeroMascot";
import { TrustedClients } from "@/components/landing/TrustedClients";
// Below-the-fold contact form — defer its heavy deps (zod, react-hook-form,
// Supabase auth client) out of the initial landing-page bundle.
const TestimonialsCarousel = lazy(() =>
  import("@/components/landing/TestimonialsCarousel").then((m) => ({
    default: m.TestimonialsCarousel,
  })),
);
const EnquiryForm = lazy(() =>
  import("@/components/EnquiryForm").then((m) => ({ default: m.EnquiryForm })),
);
import { Badge } from "@/components/ui/badge";

const steps = [
  {
    title: "Verified, Compliant Network",
    body: "Every buyer and seller undergoes rigorous multi-level KYC and GSTIN verification — only legitimate, tax-compliant businesses trade on LohaLink.",
  },
  {
    title: "Live Digital Marketplace",
    body: "Procurement officers browse a real-time market feed with AI-benchmarked prices, photo-verified lots, and transparent quality ratings for confident sourcing.",
  },
  {
    title: "Secure Escrow Payments",
    body: "Funds are locked in an RBI-grade nodal escrow the moment an order is placed — released to the seller only after the buyer receives and approves the material.",
  },
  {
    title: "Frictionless Fulfilment & Settlement",
    body: "Live truck tracking, digital weighbridge slips, and instant escrow payout on delivery — every trade closes transparently within hours of dispatch.",
  },
];


const Landing = () => (
  <>

    {/* Hero */}
    <section className="relative overflow-hidden bg-nav text-nav-foreground">
      <div className="absolute inset-0">
        <video
          src={heroVideo}
          autoPlay
          loop
          muted
          playsInline
          preload="metadata"
          aria-hidden="true"
          className="h-full w-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-nav/85" />
      </div>
      <img
        src={logo}
        alt=""
        aria-hidden="true"
        loading="lazy"
        decoding="async"
        // @ts-expect-error fetchpriority is a valid HTML attribute
        fetchpriority="low"
        className="pointer-events-none absolute -right-10 top-1/2 hidden h-[120%] -translate-y-1/2 opacity-[0.07] md:block"
      />
      <div className="container relative grid gap-6 py-14 md:py-20 lg:py-24">
        <div className="max-w-2xl">
          <span className="inline-flex items-center gap-2 rounded-full bg-accent/15 px-3 py-1 text-xs font-bold uppercase tracking-widest text-accent">
            <ShieldCheck className="h-3.5 w-3.5" /> For Foundries & Buyers · 3,400+ Verified Yards
          </span>
          <h1 className="mt-4 text-4xl font-bold leading-tight md:text-5xl lg:text-6xl">
            Buy Metal,{" "}
            <span className="text-accent">Tension Free</span>
          </h1>
          <p className="mt-4 max-w-xl text-base text-nav-foreground/85 md:text-lg">
            Buy verified HMS, sponge iron and MS billets from 3,400+ GST-verified
            yards across India. AI-benchmarked pricing, RBI-grade escrow — your
            payment releases only after you approve the material.
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Button
              asChild
              size="lg"
              className="bg-accent text-accent-foreground hover:bg-accent/90"
            >
              <Link to="/buyer">
                Browse Verified Lots <ArrowRight className="ml-1 h-4 w-4" />
              </Link>
            </Button>
            <Link
              to="/seller"
              className="text-sm font-semibold text-nav-foreground/80 underline-offset-4 hover:text-accent hover:underline"
            >
              Are you a scrap yard? List your material →
            </Link>
          </div>
        </div>
        {/* Mascot pops up from the bottom edge of the hero box */}
        <div className="pointer-events-none absolute bottom-0 right-4 hidden md:block">
          <HeroMascot />
        </div>
      </div>
    </section>

    {/* Trust strip */}
    <section className="border-b border-border bg-card">
      <div className="container grid grid-cols-2 gap-4 py-5 md:grid-cols-4">
        {[
          { k: "1,000 T", v: "Tonnage traded" },
          { k: "200+", v: "Verified yards" },
          { k: "100+", v: "Foundry buyers" },
          { k: "₹25 Cr", v: "Escrow processed" },
        ].map((s) => (
          <div key={s.v} className="text-center md:text-left">
            <div className="text-xl font-bold text-foreground md:text-2xl">{s.k}</div>
            <div className="text-xs text-muted-foreground">{s.v}</div>
          </div>
        ))}
      </div>
    </section>

    {/* Live listings showcase */}
    <LiveListingsShowcase />

    {/* Non-ferrous / other materials live now */}
    <LiveListingsShowcase
      scope="other"
      compact
      eyebrow="Beyond iron scrap"
      heading="Other materials available now"
      subheading="Dolomite, minerals and other non-ferrous lots listed live on LohaLink."
    />


    {/* Why foundries choose LohaLink (buyer-benefit block) */}
    <section className="container py-12">
      <div className="mx-auto max-w-2xl text-center">
        <span className="inline-flex items-center gap-2 rounded-full bg-info/15 px-3 py-1 text-xs font-bold uppercase tracking-widest text-info">
          <Factory className="h-3.5 w-3.5" /> Built for Foundries
        </span>
        <h2 className="mt-3 text-2xl font-bold md:text-3xl">
          Why procurement teams choose LohaLink
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Stop chasing brokers. Source verified scrap with full price, quality and payment protection.
        </p>
      </div>

      <div className="mt-8 grid gap-5 md:grid-cols-3">
        <Card className="overflow-hidden border-info/30">
          <div className="relative aspect-[16/10] overflow-hidden">
            <img
              src={benefitPrice}
              alt="AI-benchmarked scrap metal pricing illustration"
              loading="lazy"
              width={1024}
              height={1024}
              className="h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-card via-card/20 to-transparent" />
            <Gauge className="absolute left-4 top-4 h-7 w-7 text-info drop-shadow-lg" />
          </div>
          <CardContent className="space-y-2 p-6">
            <h3 className="text-lg font-bold">AI Price Benchmarks</h3>
            <p className="text-sm text-muted-foreground">
              Every lot carries a live ±band against MCX & regional indices, so you know you're paying the right rate.
            </p>
          </CardContent>
        </Card>
        <Card className="overflow-hidden border-accent/30">
          <div className="relative aspect-[16/10] overflow-hidden">
            <img
              src={benefitEscrow}
              alt="Secure escrow protection vault illustration"
              loading="lazy"
              width={1024}
              height={1024}
              className="h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-card via-card/20 to-transparent" />
            <Lock className="absolute left-4 top-4 h-7 w-7 text-accent drop-shadow-lg" />
          </div>
          <CardContent className="space-y-2 p-6">
            <h3 className="text-lg font-bold">Escrow Protection</h3>
            <p className="text-sm text-muted-foreground">
              Funds sit in an RBI-grade nodal account. Released to the seller only after you receive and approve the material.
            </p>
          </CardContent>
        </Card>
        <Card className="overflow-hidden border-success/30">
          <div className="relative aspect-[16/10] overflow-hidden">
            <img
              src={benefitVerified}
              alt="Verified scrap metal yard aerial view"
              loading="lazy"
              width={1024}
              height={1024}
              className="h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-card via-card/20 to-transparent" />
            <BadgeCheck className="absolute left-4 top-4 h-7 w-7 text-success drop-shadow-lg" />
          </div>
          <CardContent className="space-y-2 p-6">
            <h3 className="text-lg font-bold">Verified Yards Only</h3>
            <p className="text-sm text-muted-foreground">
              KYC + GSTIN + photo-verified inventory. Trade with legitimate, tax-compliant suppliers — never a faceless broker.
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="mt-8 text-center">
        <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90">
          <Link to="/buyer">
            Browse Verified Lots <ArrowRight className="ml-1 h-4 w-4" />
          </Link>
        </Button>
      </div>
    </section>

    {/* How it works */}
    <section className="border-t border-border bg-muted/30">
      <div className="container py-14 md:py-20">
        <div className="mx-auto max-w-2xl text-center">
          <span className="inline-flex items-center gap-2 rounded-full bg-accent/15 px-3 py-1 text-xs font-bold uppercase tracking-widest text-accent">
            How LohaLink Works
          </span>
          <h2 className="mt-3 text-2xl font-bold md:text-3xl lg:text-4xl">
            From sourcing to settlement — in one transparent flow
          </h2>
          <p className="mt-3 text-sm text-muted-foreground md:text-base">
            Four steps that turn unorganised scrap trade into a verified, escrow-protected, AI-driven supply chain.
          </p>
        </div>

        <div className="mt-10 grid gap-8 lg:grid-cols-2 lg:items-center">
          <div className="overflow-hidden rounded-2xl border border-border shadow-lg">
            <img
            src={howItWorks}
            alt="Four-panel illustration showing LohaLink's verification, marketplace, escrow payment, and delivery workflow"
            loading="lazy"
            decoding="async"
            className="h-full w-full object-cover"
            />
          </div>

          <ol className="space-y-5">
            {steps.map((s, i) => (
              <li
                key={s.title}
                className="flex gap-4 rounded-xl border border-border bg-card p-5 shadow-sm transition-shadow hover:shadow-md"
              >
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-accent text-base font-bold text-accent-foreground">
                  {i + 1}
                </div>
                <div>
                  <h3 className="text-base font-bold md:text-lg">{s.title}</h3>
                  <p className="mt-1 text-sm text-muted-foreground">{s.body}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </section>

    {/* EPR Credit Brokerage promo */}
    <section aria-labelledby="landing-epr-heading" className="border-y border-border bg-muted/20 py-14 md:py-20">
      <div className="container grid gap-8 lg:grid-cols-[1.1fr_0.9fr] lg:items-center">
        <div>
          <span className="inline-flex items-center gap-2 rounded-full bg-success/15 px-3 py-1 text-xs font-bold uppercase tracking-widest text-success">
            <Recycle className="h-3.5 w-3.5" /> New service
          </span>
          <h2 id="landing-epr-heading" className="mt-3 text-2xl font-bold md:text-3xl lg:text-4xl">
            EPR Credit Brokerage — close your compliance targets, not your margin
          </h2>
          <p className="mt-3 text-sm text-muted-foreground md:text-base">
            LohaLink now brokers Extended Producer Responsibility credits for plastic
            waste, e-waste, battery waste, waste tyre and used oil. Category-wise
            obligation modelling, CPCB-registered recyclers only, escrow-protected
            settlement and an audit-ready documentation pack for your annual returns.
          </p>
          <ul className="mt-5 grid gap-2 text-sm text-muted-foreground sm:grid-cols-2">
            <li className="inline-flex items-center gap-2"><BadgeCheck className="h-4 w-4 text-accent" /> Verified recycler network</li>
            <li className="inline-flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-accent" /> Escrow-protected purchase</li>
            <li className="inline-flex items-center gap-2"><Gauge className="h-4 w-4 text-accent" /> Multi-material cost estimator</li>
            <li className="inline-flex items-center gap-2"><Sparkles className="h-4 w-4 text-accent" /> BRSR &amp; ESG ready records</li>
          </ul>
          <div className="mt-6 flex flex-wrap gap-3">
            <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90">
              <Link to="/epr">
                Explore EPR brokerage <ArrowRight className="ml-1 h-4 w-4" />
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline">
              <Link to="/epr#epr-estimator">Estimate obligation cost</Link>
            </Button>
          </div>
        </div>
        <Link to="/epr" className="group overflow-hidden rounded-2xl border border-border shadow-lg">
          <img
            src={eprHero}
            alt="Baled plastic and metal waste at an Indian recycling facility that generates EPR credits"
            loading="lazy"
            width={1216}
            height={630}
            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
          />
        </Link>
      </div>
    </section>

    {/* Trusted clients & factories */}
    <TrustedClients />

    {/* Coming Soon — New Materials */}
    <section id="coming-soon" className="container py-14 md:py-20 scroll-mt-24">
      <div className="mx-auto max-w-2xl text-center">
        <span className="inline-flex items-center gap-2 rounded-full bg-accent/15 px-3 py-1 text-xs font-bold uppercase tracking-widest text-accent">
          <Sparkles className="h-3.5 w-3.5" /> Expanding our catalogue
        </span>
        <h2 className="mt-3 text-2xl font-bold md:text-3xl lg:text-4xl">
          New materials — coming soon
        </h2>
        <p className="mt-3 text-sm text-muted-foreground md:text-base">
          We're onboarding verified suppliers for these high-demand industrial raw materials.
          Tap any card for specs, sample images, and to be notified when trading goes live.
          {" "}
          <Link to="/how-it-works" className="font-semibold text-accent underline-offset-4 hover:underline">
            See how every LohaLink trade works
          </Link>{" "}
          or{" "}
          <Link to="/rates" className="font-semibold text-accent underline-offset-4 hover:underline">
            view live scrap rates
          </Link>.
        </p>
      </div>

      <div className="mt-10 grid gap-3 grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
        {COMING_SOON_MATERIALS.map((m) => (
          <Link
            key={m.slug}
            to={`/coming-soon/${m.slug}`}
            aria-label={`${m.name} — view details`}
            className="group focus:outline-none focus-visible:ring-2 focus-visible:ring-accent rounded-lg"
          >
            <Card className="overflow-hidden transition-shadow group-hover:shadow-md">
              <div className="relative aspect-square overflow-hidden">
                <img
                  src={m.img}
                  alt={`${m.name} raw material`}
                  loading="lazy"
                  width={512}
                  height={512}
                  className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                />
                <Badge className="absolute left-1.5 top-1.5 bg-accent text-accent-foreground text-[9px] px-1.5 py-0.5">
                  Coming Soon
                </Badge>
              </div>
              <CardContent className="space-y-0.5 p-2">
                <h3 className="text-xs font-bold leading-tight group-hover:text-accent">{m.name}</h3>
                <p className="text-[11px] text-muted-foreground leading-snug line-clamp-2">{m.shortDesc}</p>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>
    </section>

    <Suspense fallback={<div className="container py-14 md:py-20" style={{ minHeight: 360 }} />}>
      <TestimonialsCarousel />
    </Suspense>

    {/* Demoted seller band */}
    <section className="relative overflow-hidden border-t border-border">
      <img
        src={sellerBand}
        alt="Scrap yard owner photographing steel inventory with smartphone"
        loading="lazy"
        width={1280}
        height={960}
        className="absolute inset-0 h-full w-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-nav/95 via-nav/80 to-nav/40" />
      <div className="container relative grid items-center gap-6 py-14 md:grid-cols-[1fr_auto] md:py-20">
        <div className="max-w-xl">
          <span className="inline-flex items-center gap-2 rounded-full bg-accent/20 px-3 py-1 text-xs font-bold uppercase tracking-widest text-accent">
            <Building2 className="h-3.5 w-3.5" /> For Scrap Yards
          </span>
          <h2 className="mt-3 text-2xl font-bold text-nav-foreground md:text-3xl">
            Have scrap to sell?
          </h2>
          <p className="mt-2 text-sm text-nav-foreground/80 md:text-base">
            List a lot in 60 seconds — snap a photo, our AI suggests the category,
            drop a GPS pin, set your price. Reach 100+ foundry buyers with escrow-backed payment.
          </p>
        </div>
        <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90">
          <Link to="/seller">
            List Your Scrap <ArrowRight className="ml-1 h-4 w-4" />
          </Link>
        </Button>
      </div>
    </section>

    {/* Enquiry Section */}
    <section
      aria-labelledby="landing-enquiry-heading"
      className="border-t border-border bg-muted/20 py-14 md:py-20"
    >
      <div className="container max-w-3xl">
        <Badge variant="outline" className="mb-3">
          Get in Touch
        </Badge>
        <h2
          id="landing-enquiry-heading"
          className="text-2xl font-bold tracking-tight md:text-3xl"
        >
          Have a question? Send us an enquiry
        </h2>
        <p className="mt-3 text-sm text-muted-foreground md:text-base">
          Bulk procurement, yard onboarding, escrow or pricing — drop a
          message and our team will respond within one business day.
        </p>
        <div className="mt-8">
          <Suspense fallback={<div className="h-64" />}>
            <EnquiryForm source="landing" />
          </Suspense>
        </div>
      </div>
    </section>
  </>
);

export default Landing;
